// Okresy kontraktu — kanoniczna mapa kodu na zakres dat.
//
// Q1-Q4 (3 miesiace, kwartal), C1-C3 (4 miesiace, tertial), H1-H2 (6 miesiecy, polrocze).
// Mapowanie kod -> (start_month_0idx, durationMonths) jest singletonem;
// uzywane przez ContractFormPage do auto-wypelniania pol "od"/"do" oraz przez
// backend (SQL function contract_period_range w migracji v245).
//
// Jesli dodajesz nowy kod, zaktualizuj:
//   - PERIOD_CONFIG ponizej
//   - PERIOD_OPTIONS (ordering w dropdown)
//   - schema_manager.rs: CHECK constraint contracts_quarter_check + funkcja contract_period_range
//   - routes/contracts.rs: jesli jest dodatkowy match na quarter w Rust
//   - test: frontend/src/features/contracts/__tests__/contract-period.test.ts

export type PeriodKind = "quarter" | "tertial" | "half";

interface PeriodConfig {
	readonly code: string;
	readonly kind: PeriodKind;
	/** 0-indexed month (styczen = 0) zgodnie z `new Date(year, month, day)`. */
	readonly startMonth: number;
	readonly durationMonths: number;
	/** Etykieta w dropdownie, np. "Q1 (1-3)". */
	readonly label: string;
}

const PERIOD_CONFIG: Record<string, PeriodConfig> = {
	Q1: { code: "Q1", kind: "quarter", startMonth: 0, durationMonths: 3, label: "Q1 (1-3)" },
	Q2: { code: "Q2", kind: "quarter", startMonth: 3, durationMonths: 3, label: "Q2 (4-6)" },
	Q3: { code: "Q3", kind: "quarter", startMonth: 6, durationMonths: 3, label: "Q3 (7-9)" },
	Q4: { code: "Q4", kind: "quarter", startMonth: 9, durationMonths: 3, label: "Q4 (10-12)" },
	C1: { code: "C1", kind: "tertial", startMonth: 0, durationMonths: 4, label: "C1 (1-4)" },
	C2: { code: "C2", kind: "tertial", startMonth: 4, durationMonths: 4, label: "C2 (5-8)" },
	C3: { code: "C3", kind: "tertial", startMonth: 8, durationMonths: 4, label: "C3 (9-12)" },
	H1: { code: "H1", kind: "half", startMonth: 0, durationMonths: 6, label: "H1 (1-6)" },
	H2: { code: "H2", kind: "half", startMonth: 6, durationMonths: 6, label: "H2 (7-12)" },
};

/** Lista okresow w kolejnosci wyswietlania w dropdownie, pogrupowana wedlug `kind`. */
export const PERIOD_OPTIONS: ReadonlyArray<PeriodConfig> = [
	PERIOD_CONFIG.Q1,
	PERIOD_CONFIG.Q2,
	PERIOD_CONFIG.Q3,
	PERIOD_CONFIG.Q4,
	PERIOD_CONFIG.C1,
	PERIOD_CONFIG.C2,
	PERIOD_CONFIG.C3,
	PERIOD_CONFIG.H1,
	PERIOD_CONFIG.H2,
];

/** Domyslny okres dla nowo tworzonych kontraktow. */
export const DEFAULT_CONTRACT_PERIOD = "Q1";

/**
 * Rok kontraktu sprowadzony do liczby, z ktorej `new Date` na pewno zlozy
 * poprawna date w formacie ISO `yyyy-mm-dd`.
 *
 * Zgloszenie 2026-08-13: pole "Rok" w formularzu kontraktu wpisywalo do stanu
 * surowe `Number(e.target.value)`, wiec wartosc, ktorej `Date` nie potrafi
 * wyrazic (NaN, Infinity, notacja wykladnicza typu 2026e3), dawala Invalid
 * Date. Zakres okresu schodzil wtedy do napisu "NaN-NaN-NaN", trafial jako
 * domyslne "Od"/"Do" do KAZDEJ importowanej apteki, a `<input type="date">`
 * odmawial go wyswietlic — pola wygladaly na puste.
 *
 * Gorna/dolna granica (4 cyfry) nie jest regula biznesowa, tylko warunkiem
 * formatu: rok 5-cyfrowy zlamalby kontrakt `yyyy-mm-dd` z backendem i
 * `<input type="date">`. Zakres dopuszczalny biznesowo pilnuje `min`/`max`
 * na polu formularza.
 */
export function normalizeContractYear(
	year: unknown,
	fallback: number = new Date().getFullYear(),
): number {
	const parsed = typeof year === "number" ? year : Number(year);
	return Number.isInteger(parsed) && parsed >= 1000 && parsed <= 9999 ? parsed : fallback;
}

/**
 * Zwraca date startu i konca okresu jako ISO yyyy-mm-dd (data lokalna, bez TZ shift).
 *
 * Dla nieznanego kodu okresu zwraca zakres Q1 (fallback do dotychczasowego zachowania).
 * `new Date(year, month, 0)` daje ostatni dzien poprzedniego miesiaca — przekazujemy
 * `startMonth + durationMonths` zeby otrzymac ostatni dzien okresu.
 *
 * UWAGA: nie uzywamy `toISOString()` bo konwertuje na UTC i przy strefie +01/+02
 * (Polska) `new Date(2026, 0, 1)` -> "2025-12-31" w UTC. Formatujemy z lokalnych
 * komponentow (`getFullYear/getMonth/getDate`).
 */
export function getContractDateRange(
	contractYear: number,
	contractPeriod: string,
): { startDate: string; endDate: string } {
	const cfg = PERIOD_CONFIG[contractPeriod] ?? PERIOD_CONFIG[DEFAULT_CONTRACT_PERIOD];
	// Rok normalizowany TUTAJ, bo przez te funkcje przechodza wszyscy wolajacy
	// (formularz kontraktu, import aptek, selektor aptek, dymek instrukcji).
	const year = normalizeContractYear(contractYear);
	const startDate = new Date(year, cfg.startMonth, 1);
	const endDate = new Date(year, cfg.startMonth + cfg.durationMonths, 0);
	return {
		startDate: formatLocalIso(startDate),
		endDate: formatLocalIso(endDate),
	};
}

function formatLocalIso(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/** Czy kod okresu jest znany. */
export function isValidPeriodCode(code: string): boolean {
	return code in PERIOD_CONFIG;
}

/**
 * Kody wszystkich okresow obejmujacych podana date (w jej roku), np. dla
 * 12 sierpnia: ["Q3", "C2", "H2"]. Uzywane przez filtr "Biezace okresy" na
 * liscie rozliczen — literalny biezacy kwartal ukrywal kontrakty
 * 4-miesieczne (C*) i polroczne (H*), ktore wlasnie trwaja.
 */
export function periodCodesContaining(date: Date): string[] {
	const month = date.getMonth();
	return PERIOD_OPTIONS.filter(
		(cfg) => month >= cfg.startMonth && month < cfg.startMonth + cfg.durationMonths,
	).map((cfg) => cfg.code);
}
